import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AdminOnlyRoute } from '@/components/auth/AdminOnlyRoute';
import { PublicToolRoute } from '@/components/routing/PublicToolRoute';
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { usePerformanceOptimization } from "@/hooks/usePerformanceOptimization";
import {
  LazyIndex, LazyAuth, LazyResetPassword, LazyAdmin, LazyAssessmentHistory, LazyAssessments,
  LazyIntegralHub, LazyProfile, LazyMentor, LazyMentorBoard, LazyMentorArc, LazyNotFound,
  LazyTPSProfile, LazyTPSOnboarding, LazyTPSAssessmentHub, LazyTPSDomainAssessment,
  LazyTPSDomainResults, LazyTPSFinalSynthesis, LazyLiker, LazyLikerSession, LazyLikerCollections,
  LazyLikerCollectionDetail, LazyAstrology, LazyScience, LazyScienceArticle, PageWrapper,
  LazyIntegralAssessment, LazyConnections, LazyLeaderboard, LazyTestSessions, LazyPublicProfile,
  LazyCommons, LazyMail, LazyCommunity, LazyJoinInvite, LazyExplore, LazyNotifications, LazyFeed, LazyBookmarks, LazyCards,
  LazyCharacterQuiz, LazyQuizDirectory, LazyQuizRouter, LazyDemoPersonalityResults,
  LazyDemoIntegralResults, LazyDemoMentor, LazyDemoTPSProfile, LazyDemoHub, LazyInsights,
  LazyUnifiedPersonality, LazyGuestResultsPreview, LazyDisambiguationQuizSession,
  LazyFrameworkClarify,
  LazySharedResultsView, LazyTestDirectory, LazyCareer, LazyRelationships, LazySubscription,
  LazyRelationshipsHub, LazyRelationshipDetail, LazyRelationshipGroups,
  LazyRelationshipMessages, LazyRelationshipInvite, LazyAgentSessions,
  LazyEnterprise, LazyCompatibilitySearch, LazySocial, LazyMbtiLevelsMatrix,
  LazyResonanceAuditPanel, LazyAtlas, LazyDocs, LazyFrameworkDetail, LazyBrandPreview,
  LazyMotionLab, LazyDailyReflection,
  LazyCoinShop,
  LazyEarn,
  LazyPsychosophy,
  LazyPoliticalCompass,
  LazySkillTree,
  LazyCoinRequests,
  LazyModeration,
  LazyBroadcasts,
  LazyUnsubscribe,
  LazySignalHealth,
  LazyLikerCatalog,
  LazyIQTest,
  LazyCognitiveReport,
  LazyIQShared,
  LazyReportReader,
  LazyReports,
} from './components/layout/LazyPages';
import { ProductionWrapper } from './components/layout/ProductionWrapper';
import React from 'react';
import '@/utils/debugCleanup'; // Initialize debug cleanup
import { ProductionReadinessChecker } from '@/utils/productionReadinessChecker';
import '@/utils/bundleOptimizer'; // Initialize bundle optimization
import { useScoringOverrides } from './hooks/useScoringOverrides';
import { ImpersonationProvider } from '@/contexts/ImpersonationContext';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { MotionProvider } from '@/lib/motion';
import { PageTransition } from '@/components/ui/page-transition';
import { AppShell } from '@/components/layout/AppShell';
import { OptionalAppShell } from '@/components/layout/OptionalAppShell';
import { fixCognitiveFunctionsStack } from '@/utils/migrations/fixCognitiveFunctionsStack';
import { SuspensionGate } from '@/components/auth/SuspensionGate';


// Redirect component for /tests/:testSlug → /quizzes/:testSlug
const TestRedirect = () => {
  const params = React.useMemo(() => window.location.pathname.split('/').pop(), []);
  return <Navigate to={`/quizzes/${params}`} replace />;
};

const AppContent = () => {
  usePerformanceOptimization(); // Initialize performance monitoring

  // Load global scoring overrides (cached in localStorage for sync access)
  useScoringOverrides();
  
  // Initialize production optimizations
  React.useEffect(() => {
    ProductionReadinessChecker.optimizeForProduction();
  }, []);

  // One-time client migration: repair cached Cognitive Functions quiz
  // results (e.g. "Te-Fe Stack (XXXX)") using the corrected Jungian stack.
  React.useEffect(() => {
    fixCognitiveFunctionsStack();
  }, []);
  
  return (
    <MotionProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ImpersonationBanner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SuspensionGate>
        <PageTransition>
        <Routes>
            <Route path="/" element={<PageWrapper><LazyIndex /></PageWrapper>} />
            <Route path="/dev/brand" element={<PageWrapper><LazyBrandPreview /></PageWrapper>} />
            <Route path="/dev/motion" element={<PageWrapper><LazyMotionLab /></PageWrapper>} />
            <Route
              path="/reflect"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyDailyReflection /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/auth" 
              element={
                <PageWrapper><LazyAuth /></PageWrapper>
              } 
            />
            <Route 
              path="/auth/reset-password" 
              element={
                <PageWrapper><LazyResetPassword /></PageWrapper>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyProfile /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyAdmin /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/mbti-levels" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyMbtiLevelsMatrix /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/resonance"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyResonanceAuditPanel /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/atlas"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyAtlas /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/docs"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyDocs /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/coin-requests"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyCoinRequests /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/broadcasts"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyBroadcasts /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/unsubscribe/:token"
              element={<PageWrapper><LazyUnsubscribe /></PageWrapper>}
            />
            <Route
              path="/admin/moderation"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyModeration /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/signals"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazySignalHealth /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/liker-catalog"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyLikerCatalog /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop"
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyCoinShop /></AppShell></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/earn"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyEarn /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:id"
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyReportReader /></AppShell></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyReports /></AppShell></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/psychosophy"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyPsychosophy /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/political-compass"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyPoliticalCompass /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cognitive-report"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyCognitiveReport /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/iq-test"
              element={<PageWrapper><LazyIQTest /></PageWrapper>}
            />
            <Route
              path="/iq/shared/:token"
              element={<PageWrapper><LazyIQShared /></PageWrapper>}
            />
            <Route 
              path="/history" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyAssessmentHistory /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/agent/sessions" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyAgentSessions /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/mentor" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyMentorBoard /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/mentor/stream" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyMentor /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/mentor/arc" 
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyMentorArc /></AppShell></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/insights" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyInsights /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/personality" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyUnifiedPersonality /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/integral" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyIntegralHub /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/skill-tree"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazySkillTree /></PageWrapper>
                </ProtectedRoute>
              }
            />

            <Route
              path="/personality/clarify/:cardKey"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyFrameworkClarify /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="/integral/assess" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyIntegralAssessment /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/integral/results/:assessmentId"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyIntegralAssessment /></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/tps-profile" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyTPSProfile /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/tps-onboarding" 
              element={<PageWrapper><LazyTPSOnboarding /></PageWrapper>} 
            />
            <Route 
              path="/tps-assessment-hub" 
              element={<PageWrapper><LazyTPSAssessmentHub /></PageWrapper>} 
            />
            <Route 
              path="/tps-domain-assessment/:domain" 
              element={<PageWrapper><LazyTPSDomainAssessment /></PageWrapper>} 
            />
            <Route 
              path="/tps-domain-results/:domain" 
              element={<PageWrapper><LazyTPSDomainResults /></PageWrapper>} 
            />
            <Route 
              path="/tps-final-synthesis" 
              element={<PageWrapper><LazyTPSFinalSynthesis /></PageWrapper>} 
            />
            <Route 
              path="/results/preview" 
              element={<PageWrapper><LazyGuestResultsPreview /></PageWrapper>} 
            />
            <Route 
              path="/results/shared" 
              element={<PageWrapper><LazySharedResultsView /></PageWrapper>} 
            />
            <Route 
              path="/assessments" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyAssessments /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/test" 
              element={<Navigate to="/tps-assessment-hub" replace />} 
            />
            <Route 
              path="/assessment/tps" 
              element={<Navigate to="/tps-onboarding" replace />} 
            />
            <Route 
              path="/dashboard" 
              element={<Navigate to="/" replace />} 
            />
            <Route
              path="/dashboard/framework/:systemKey"
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyFrameworkDetail /></AppShell></PageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/settings" 
              element={<Navigate to="/profile" replace />} 
            />
            <Route 
              path="/discover" 
              element={<Navigate to="/tps-onboarding" replace />} 
            />
            <Route 
              path="/connections"
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyConnections /></AppShell></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/leaderboard" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyLeaderboard /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/notifications" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyNotifications /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sessions"
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyTestSessions /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/u/:username" 
              element={
                <OptionalAppShell><PageWrapper><LazyPublicProfile /></PageWrapper></OptionalAppShell>
              }
            />
            <Route 
              path="/u/invite/:inviteCode" 
              element={
                <OptionalAppShell><PageWrapper><LazyPublicProfile /></PageWrapper></OptionalAppShell>
              }
            />
            <Route 
              path="/commons" 
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute><PageWrapper><LazyCommons /></PageWrapper></AdminOnlyRoute>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/community"
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute><PageWrapper><LazyCommunity /></PageWrapper></AdminOnlyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/join/:code"
              element={<PageWrapper><LazyJoinInvite /></PageWrapper>}
            />
            <Route 
              path="/mail" 
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute><PageWrapper><LazyMail /></PageWrapper></AdminOnlyRoute>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/explore" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyExplore /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/feed" 
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyFeed /></AppShell></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/bookmarks" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyBookmarks /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/cards" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyCards /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/quizzes" 
              element={<PageWrapper><OptionalAppShell><LazyTestDirectory /></OptionalAppShell></PageWrapper>}
            />
            <Route 
              path="/quizzes/:testSlug" 
              element={<PageWrapper><LazyQuizRouter /></PageWrapper>}
            />
            <Route 
              path="/quiz" 
              element={
                <PageWrapper><LazyQuizDirectory /></PageWrapper>
              } 
            />
            <Route 
              path="/quiz/:packSlug" 
              element={
                <PageWrapper><LazyCharacterQuiz /></PageWrapper>
              } 
            />
            <Route 
              path="/quiz/disambiguation" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyDisambiguationQuizSession /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            {/* Legacy /tests redirects */}
            <Route path="/tests" element={<Navigate to="/quizzes" replace />} />
            <Route path="/tests/:testSlug" element={<TestRedirect />} />
            {/* Demo pages for unauthenticated users */}
            <Route 
              path="/demo" 
              element={
                <PageWrapper><LazyDemoHub /></PageWrapper>
              } 
            />
            <Route 
              path="/demo/personality" 
              element={
                <PageWrapper><LazyDemoPersonalityResults /></PageWrapper>
              }
            />
            <Route 
              path="/demo/integral" 
              element={
                <PageWrapper><LazyDemoIntegralResults /></PageWrapper>
              } 
            />
            <Route 
              path="/demo/mentor" 
              element={
                <PageWrapper><LazyDemoMentor /></PageWrapper>
              } 
            />
            <Route 
              path="/demo/tps" 
              element={
                <PageWrapper><LazyDemoTPSProfile /></PageWrapper>
              } 
            />
            {/* Liker Routes */}
            <Route 
              path="/liker" 
              element={
                <PublicToolRoute slug="liker" showCrossSell={false}>
                  <PageWrapper><LazyLiker /></PageWrapper>
                </PublicToolRoute>
              } 
            />
            <Route 
              path="/liker/collections" 
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyLikerCollections /></AppShell></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/liker/collections/:id" 
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyLikerCollectionDetail /></AppShell></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/liker/:category" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyLikerSession /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            {/* Career Guidance */}
            <Route 
              path="/career" 
              element={
                <PublicToolRoute slug="career">
                  <PageWrapper><LazyCareer /></PageWrapper>
                </PublicToolRoute>
              } 
            />
            {/* Relationship Analysis */}
            <Route 
              path="/relationships" 
              element={
                <PublicToolRoute slug="relationships">
                  <PageWrapper><LazyRelationshipsHub /></PageWrapper>
                </PublicToolRoute>
              } 
            />
            {/* Deep AI report + member discovery flow (legacy surface, still linked from the hub) */}
            <Route 
              path="/relationships/report" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyRelationships /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/relationships/groups" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyRelationshipGroups /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/relationships/messages" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyRelationshipMessages /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            {/* Keep last: :id must not shadow the named sub-routes above */}
            <Route 
              path="/relationships/:id" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazyRelationshipDetail /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            {/* Public partner co-assessment invite — no account required */}
            <Route 
              path="/r/invite/:code" 
              element={<PageWrapper><LazyRelationshipInvite /></PageWrapper>} 
            />
            <Route 
              path="/subscription" 
              element={
                <ProtectedRoute>
                  <PageWrapper><LazySubscription /></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/enterprise" 
              element={
                <PublicToolRoute slug="enterprise">
                  <PageWrapper><OptionalAppShell><LazyEnterprise /></OptionalAppShell></PageWrapper>
                </PublicToolRoute>
              } 
            />
            <Route 
              path="/compatibility-search" 
              element={
                <ProtectedRoute>
                  <PageWrapper><AppShell><LazyCompatibilitySearch /></AppShell></PageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/social/*" 
              element={
                <ProtectedRoute>
                  <AdminOnlyRoute><PageWrapper><LazySocial /></PageWrapper></AdminOnlyRoute>
                </ProtectedRoute>
              } 
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route
              path="/astrology"
              element={<PageWrapper><OptionalAppShell><LazyAstrology /></OptionalAppShell></PageWrapper>}
            />
            <Route
              path="/astrology/synastry/:userId?"
              element={<PageWrapper><OptionalAppShell><LazyAstrology /></OptionalAppShell></PageWrapper>}
            />
            {/* Personality Science — public, no auth required */}
            <Route
              path="/science"
              element={<PageWrapper><OptionalAppShell><LazyScience /></OptionalAppShell></PageWrapper>}
            />
            <Route
              path="/science/:slug"
              element={<PageWrapper><OptionalAppShell><LazyScienceArticle /></OptionalAppShell></PageWrapper>}
            />
            <Route path="*" element={<PageWrapper><LazyNotFound /></PageWrapper>} />
        </Routes>
        </PageTransition>
        </SuspensionGate>
      </BrowserRouter>
    </TooltipProvider>
    </MotionProvider>
  );
};

const App = () => (
  <ProductionWrapper>
    <QueryClientProvider client={queryClient}>
      <ImpersonationProvider>
        <AppContent />
      </ImpersonationProvider>
    </QueryClientProvider>
  </ProductionWrapper>
);

export default App;
